// Durable, cross-device portal personalization client (menu drawer + Action drawer).
//
// The menu drawer's order/colors/pinned-landing-page used to live ONLY in this
// browser's localStorage. That made them per-device AND, on iOS Safari, subject
// to WebKit's ~7-day eviction of script-written localStorage — which is why
// they "kept getting forgotten" even after the logout-preserve fix. These
// helpers talk to the BFF's per-user store (GET/PUT /user/portal/menu-prefs),
// which is the durable source of truth; localStorage stays as a fast cache that
// we re-seed from the server on login/boot. The same store now carries the
// Action (⚡) drawer's personalization under an `actions` sub-blob
// ({ order: [...], colors: {...} }), namespaced so the two drawers never
// collide. The BFF merges at the top level, so a menu-only save never erases
// action prefs and vice-versa.

import { instance } from "../axios/axios";
import {
  menuOrderKey,
  menuColorsKey,
  landingPageKey,
  actionOrderKey,
  actionColorsKey,
} from "./utils";

const ENDPOINT = "/user/portal/menu-prefs";

const authHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

// Fetch the caller's saved prefs
// ({ order, colors, landingPage, actions: { order, colors } }) or null when
// they've never customized / the request fails. Never throws — callers treat a
// null/failed fetch as "keep whatever localStorage already has".
export const fetchServerMenuPrefs = async (token) => {
  try {
    const res = await instance.get(ENDPOINT, { headers: authHeaders(token) });
    return res?.data?.data ?? null;
  } catch {
    return null;
  }
};

// Upsert the caller's prefs. Fire-and-forget: the localStorage write already
// happened (instant UX), so a failed sync just means this device's change
// reaches the server on the next successful save. The BFF merges top-level keys,
// so callers only send the keys they own (ButtonsForMobile sends menu fields;
// ActionButtonsDrawer sends `actions`). Never throws.
export const saveServerMenuPrefs = (token, prefs) => {
  if (!token) return Promise.resolve(null);
  // Only include the top-level keys THIS caller owns. The menu drawer passes
  // { order, colors, landingPage } (all three present; an explicit null means
  // "clear"); the Action drawer passes only { actions }. The BFF MERGES the
  // keys present in the body and treats an explicitly-present null as a clear,
  // so if we injected order/colors/landingPage as null for a caller that did
  // not pass them, an Action-drawer save would wipe the user's menu prefs
  // (and vice-versa). Omit absent keys so the other namespace is preserved.
  const body = {};
  if (prefs && "order" in prefs) {
    body.order = Array.isArray(prefs.order) ? prefs.order : null;
  }
  if (prefs && "colors" in prefs) {
    body.colors =
      prefs.colors && typeof prefs.colors === "object" ? prefs.colors : {};
  }
  if (prefs && "landingPage" in prefs) {
    body.landingPage =
      typeof prefs.landingPage === "string" ? prefs.landingPage : null;
  }
  if (prefs?.actions && typeof prefs.actions === "object") {
    body.actions = {
      order: Array.isArray(prefs.actions.order) ? prefs.actions.order : null,
      colors:
        prefs.actions.colors && typeof prefs.actions.colors === "object"
          ? prefs.actions.colors
          : {},
    };
  }
  return instance
    .put(ENDPOINT, body, { headers: authHeaders(token) })
    .then((res) => res?.data?.data ?? null)
    .catch(() => null);
};

// Pull the durable server copy and re-seed this device's localStorage under the
// same keys the drawers (ButtonsForMobile + ActionButtonsDrawer) and Home's
// landing-page resolver read. Returns the prefs (so a caller can also setState)
// or null. Server wins on conflict because it's the durable, cross-device
// source of truth.
export const hydrateLocalMenuPrefs = async (userKey, token) => {
  const prefs = await fetchServerMenuPrefs(token);
  if (!prefs) return null;
  try {
    if (Array.isArray(prefs.order)) {
      localStorage.setItem(menuOrderKey(userKey), JSON.stringify(prefs.order));
    } else {
      localStorage.removeItem(menuOrderKey(userKey));
    }
    if (prefs.colors && typeof prefs.colors === "object") {
      localStorage.setItem(menuColorsKey(userKey), JSON.stringify(prefs.colors));
    }
    if (prefs.landingPage) {
      localStorage.setItem(landingPageKey(userKey), prefs.landingPage);
    } else {
      localStorage.removeItem(landingPageKey(userKey));
    }
    const act = prefs.actions && typeof prefs.actions === "object" ? prefs.actions : {};
    if (Array.isArray(act.order)) {
      localStorage.setItem(actionOrderKey(userKey), JSON.stringify(act.order));
    } else {
      localStorage.removeItem(actionOrderKey(userKey));
    }
    if (act.colors && typeof act.colors === "object") {
      localStorage.setItem(actionColorsKey(userKey), JSON.stringify(act.colors));
    } else {
      localStorage.removeItem(actionColorsKey(userKey));
    }
  } catch {
    /* private mode / quota — the in-memory return value still works */
  }
  return prefs;
};
